import { createContext, useEffect, useState } from 'react';
import { axios, isUndNull, isUndNullEmp, isValidEmail, getResponse, settings } from '../helpers/main';
import { LoginResponse } from '../classess/account/loginResponse';
import { User } from '../classess/user/user';
import { TokenUser } from '../classess/token/tokenUser';
import { Role } from '../classess/role/role';

// eslint-disable-next-line
export const userAuthContextType = {
    id: '',
    firstName: '',
    lastName: '',
    token: {
        id: '',
        token: '',
    },
    role: {
        id: '',
    },
    urlPicture: '',
};

/**
 * @typedef {Object} AuthContextType
 * @property {userAuthContextType} user
 * @property {function({email?: string, password?: string, rememberMe?: boolean, userDataSave?: boolean, reCaptchaToken?: string, languageId?: string, subMethod?: string}): Promise<Object>} login
 * @property {function({subMethod?: string}): Object} logout
 * @property {function({userData?: Object, subMethod?: string}): Object} isLoged
 * @property {function({subMethod?: string}): Object} removeUser
 * @property {function({_user?: Object, _token?: Object, _role?: Object, languageId?: string, subMethod?: string}): Object} updateUser
 * @property {function({languageId?: string, subMethod?: string}): Object} userRole
 */
export const AuthContext = createContext();

let requestData = {};

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactElement}
 */
export default function AuthProvider({ children }) {
    const [userLS, setUserLS] = useState(
        JSON.parse(localStorage?.getItem(settings?.localStorage?.userId)) || null
    ); //user LocalStore

    useEffect(() => {
        try {
            localStorage?.setItem(settings?.localStorage?.userId, JSON.stringify(userLS));
        } catch (ex) {
            localStorage?.removeItem(settings?.localStorage?.userId);
        }
    }, [userLS]);

    const contextValue = {
        user: userLS,
        async login({
            email = '',
            password = '',
            rememberMe = false,
            userDataSave = false,
            reCaptchaToken = '',
            languageId = 'en',
            subMethod = '',
        }) {
            let response = getResponse({ type: 0 });
            const currentMethod = 'AuthProvider - login';
            try {
                //#region variables
                let user = User?.getClass({}, false);
                let token = TokenUser?.class({});
                let role = Role?.class({}, false);
                let loginData = LoginResponse?.class({});
                //#endregion variables

                //#region login
                requestData = {
                    url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.accounts}${settings?.backend?.urls?.login}/`,
                    data: { email, password, rememberMe, reCaptchaToken, languageId },
                    headers: '',
                    subMethod: currentMethod
                };
                response = await axios?.post(requestData);

                if (!response?.isSuccess || response?.isWarning) {
                    return response;
                }
                //#endregion login

                //#region set data
                loginData = LoginResponse?.class(response?.result);
                user = User?.getClass(loginData?.user);
                token = TokenUser?.class(loginData?.token);
                role = Role?.class(loginData?.role);

                user.token = token;
                user.role = role;
                //#endregion set data

                //#region save (user local)
                if (userDataSave) {
                    response = this?.updateUser({ _user: user, _token: token, _role: role, languageId: languageId, subMethod: currentMethod, });
                    if (!response?.isSuccess || response?.isWarning) {
                        return response;
                    }
                } else {
                    response = this?.logout({ subMethod: currentMethod });
                    if (!response?.isSuccess || response?.isWarning) {
                        return response;
                    }
                }
                //#endregion save (user local)
            } catch (ex) {
                this?.logout({ subMethod: currentMethod });
                response = getResponse({ type: -1, msgText: `Error: ${ex?.message}<br/>`, currentMethod_: currentMethod, subMethod_: subMethod });

            }
            return response;
        },
        logout({
            subMethod = ''
        }) {
            let response = getResponse({ type: 0 });
            const currentMethod = 'AuthProvider - logout';
            try {
                response = this?.removeUser({ subMethod: currentMethod });
            } catch (ex) {
                response = getResponse({ type: -1, msgText: `Error: ${ex?.message}<br/>`, currentMethod_: currentMethod, subMethod_: subMethod });
                console.error(response);
            }
            return response;
        },
        isLoged({
            userData = undefined,
            subMethod = ''
        }) {
            let response = getResponse({ type: 0 });
            const currentMethod = 'AuthProvider - isLoged';
            try {
                //#region variables
                let userIsLoged = false;
                //#endregion variables

                userData = !isUndNullEmp({ data: userData, subMethod: currentMethod })?.result ? userData : userLS;
                if (isUndNullEmp({ data: userData, subMethod: currentMethod })?.result ||
                    isUndNullEmp({ data: userData?.token, subMethod: currentMethod })?.result ||
                    isUndNull({ data: userData?.token?.id, subMethod: currentMethod })?.result ||
                    isUndNullEmp({ data: userData?.token?.token, subMethod: currentMethod })?.result ||
                    isUndNullEmp({ data: userData?.role, subMethod: currentMethod })?.result ||
                    isUndNullEmp({ data: userData?.role?.id, subMethod: currentMethod })?.result ||
                    isUndNullEmp({ data: userData?.role?.name, subMethod: currentMethod })?.result) {
                    // this?.removeUser({ subMethod: currentMethod }); // MUTATION REMOVED (Bad setState in render)
                    userIsLoged = true;
                }

                response.result = !userIsLoged;
            } catch (ex) {
                response = getResponse({ type: -1, msgText: `Error: ${ex?.message}<br/>`, currentMethod_: currentMethod, subMethod_: subMethod });
                response.result = false;
                console.error(response);
            }
            return response;
        },
        removeUser({
            subMethod = '',
        }) {
            let response = getResponse({ type: 0 });
            const currentMethod = 'AuthProvider - removeUser';
            try {
                localStorage?.removeItem(settings?.localStorage?.userId);
                setUserLS(null);
            } catch (ex) {
                response = getResponse({ type: -1, msgText: `Error: ${ex?.message}<br/>`, currentMethod_: currentMethod, subMethod_: subMethod });
                console.error(response);
            }
            return response;
        },
        updateUser({
            _user = User?.getClass({}, false),
            _token = TokenUser?.class({}),
            _role = Role?.class({}),
            languageId = 'en',
            subMethod = '',
        }) {
            let response = getResponse({ type: 0 });
            const currentMethod = 'AuthProvider - updateUser';
            try {
                //#region variables
                let finalData = {
                    user: Object?.assign(User?.getClass(_user, false), { token: _token, role: _role }),
                };
                //#region response is warning
                let responseIsWarning = getResponse({ type: 1, currentMethod_: currentMethod, subMethod_: subMethod });
                //#endregion response is warning
                //#endregion variables

                //#region check data (user / token / token)
                if (isUndNullEmp({ data: finalData?.user, subMethod: currentMethod })?.result) {
                    responseIsWarning.msgText = `User. ${settings?.messages?.thisFieldRequired?.[languageId]}`;
                    return responseIsWarning;
                } else if (isUndNullEmp({ data: finalData?.user?.id, subMethod: currentMethod })?.result) {
                    responseIsWarning.msgText = `User / id. ${settings?.messages?.thisFieldRequired?.[languageId]}`;
                    return responseIsWarning;
                } else if (isUndNullEmp({ data: finalData?.user?.email, subMethod: currentMethod })?.result || !isValidEmail({ data: finalData?.user?.email })) {
                    responseIsWarning.msgText = `User / email. ${settings?.messages?.thisFieldRequired?.[languageId]}`;
                    return responseIsWarning;
                } else if (isUndNullEmp({ data: finalData?.user?.token, subMethod: currentMethod })?.result) {
                    responseIsWarning.msgText = `User / token. ${settings?.messages?.thisFieldRequired?.[languageId]}`;
                    return responseIsWarning;
                } else if (isUndNullEmp({ data: finalData?.user?.token?.token, subMethod: currentMethod })?.result) {
                    responseIsWarning.msgText = `User / token / token. ${settings?.messages?.thisFieldRequired?.[languageId]}`;
                    return responseIsWarning;
                } else if (isUndNullEmp({ data: finalData?.user?.role, subMethod: currentMethod })?.result) {
                    responseIsWarning.msgText = `User / role. ${settings?.messages?.thisFieldRequired?.[languageId]}`;
                    return responseIsWarning;
                } else if (isUndNullEmp({ data: finalData?.user?.role?.id, subMethod: currentMethod })?.result) {
                    responseIsWarning.msgText = `User / role. ${settings?.messages?.thisFieldRequired?.[languageId]}`;
                    return responseIsWarning;
                } else if (isUndNullEmp({ data: finalData?.user?.role?.name, subMethod: currentMethod })?.result) {
                    responseIsWarning.msgText = `User / role / name. ${settings?.messages?.thisFieldRequired?.[languageId]}`;
                    return responseIsWarning;
                }
                //#endregion check data (user / token / token)

                //#region check user / update
                if (this?.isLoged({ userData: finalData?.user, subMethod: currentMethod })?.result) {
                    setUserLS(finalData?.user);
                }
                //#endregion check user / update

                //#region final data (set)
                response.result = finalData;
                //#endregion final data (set)
            } catch (ex) {
                this?.logout({ subMethod: currentMethod });
                response = getResponse({ type: -1, msgText: `Error: ${ex?.message}<br/>`, currentMethod_: currentMethod, subMethod_: subMethod });
            }
            return response;
        },
        userRole({
            languageId = 'en',
            subMethod = '',
        }) {
            let response = getResponse({ type: 0 });
            const currentMethod = 'userRole';
            try {
                //#region variables
                //#region response is warning

                let responseIsWarning = getResponse({ type: 1, msgText: `${settings?.messages?.youDoNotHaveSufficientPrivilegesToRunThisProcess?.[languageId]}<br/>` });
                responseIsWarning.result = settings?.roles?.anonymous;
                //#endregion response is warning
                //#endregion variables

                //#region check role
                if (!this?.isLoged({ subMethod: currentMethod })?.result) {
                    return responseIsWarning;
                }

                if (!isUndNullEmp({ data: userLS, subMethod: currentMethod })?.result &&
                    !isUndNullEmp({ data: userLS?.email, subMethod: currentMethod })?.result &&
                    isValidEmail({ data: userLS?.email }) &&
                    !isUndNullEmp({ data: userLS?.role, subMethod: currentMethod })?.result &&
                    !isUndNullEmp({ data: userLS?.role?.name, subMethod: currentMethod })?.result) {
                    response.result = `${userLS.role.name.charAt(0).toLowerCase()}${userLS.role.name.slice(1)}`;
                } else {
                    return responseIsWarning;
                }
                //#endregion check role
            } catch (ex) {
                response = getResponse({ type: -1, msgText: `Error: ${ex?.message}<br/>`, currentMethod_: currentMethod, subMethod_: subMethod });
                response.result = settings?.roles?.anonymous;
            }
            return response;
        }
    };

    return <AuthContext.Provider value={contextValue}>
        {children}
    </AuthContext.Provider>
}